import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetOpportunityFiltersQuery, useListOpportunitiesQuery } from "@/state/opportunitiesApi";

export default function BusinessOpportunities() {
  const { t } = useTranslation();
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const { data: filtersData } = useGetOpportunityFiltersQuery();
  const { data: listData, isLoading } = useListOpportunitiesQuery({
    sector: selectedSector || undefined,
    country: selectedCountry || undefined,
  });

  const sectors = filtersData?.data?.sectors ?? [];
  const countries = filtersData?.data?.countries ?? [];
  const opportunities = listData?.data ?? [];
  const total = listData?.meta?.total ?? 0;

  return (
    <section id="opportunities" className="py-24 bg-[#f8f9fc]">
      <div className="w-full px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-3">{t("oppTag")}</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight">
              {t("oppTitle1")} <span className="text-[#00C2C2]">{t("oppTitle2")}</span>
            </h2>
            <p className="text-gray-500 text-base mt-4 max-w-xl leading-relaxed">
              {t("oppDesc")}
            </p>
          </div>
          <div className="text-sm text-gray-400 font-medium">
            {total} {t("oppCount", { count: total })}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-10">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center text-[#00C2C2]"><i className="ri-filter-3-line text-sm" /></div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("filterBy")}</span>
          </div>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="text-sm border border-gray-200 rounded-full px-4 py-2 bg-white text-[#0A1628] focus:outline-none focus:border-[#00C2C2] cursor-pointer transition-colors"
          >
            <option value="">All Countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={selectedSector}
            onChange={(e) => setSelectedSector(e.target.value)}
            className="text-sm border border-gray-200 rounded-full px-4 py-2 bg-white text-[#0A1628] focus:outline-none focus:border-[#00C2C2] cursor-pointer transition-colors"
          >
            <option value="">All Sectors</option>
            {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {(selectedSector || selectedCountry) && (
            <button
              onClick={() => { setSelectedSector(""); setSelectedCountry(""); }}
              className="text-xs text-[#00C2C2] font-semibold hover:underline flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line" /> {t("oppClear")}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-lg border border-gray-100 overflow-hidden">
                <div className="w-full h-48 bg-gray-100" />
                <div className="p-5">
                  <div className="h-3 bg-gray-100 rounded w-1/2 mb-3" />
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-full mb-1" />
                  <div className="h-3 bg-gray-100 rounded w-5/6 mb-4" />
                  <div className="h-8 bg-gray-100 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : opportunities.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
              <i className="ri-search-line text-xl" />
            </div>
            <p className="font-semibold text-[#0A1628]">{t("oppNoResults")}</p>
            <p className="text-sm mt-1">{t("oppNoResultsHint")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {opportunities.map((opp) => (
              <div key={opp.id} className="bg-white rounded-lg border border-gray-100 overflow-hidden hover:border-[#00C2C2]/40 hover:-translate-y-1 transition-all duration-200 group flex flex-col">
                <div className="w-full h-48 overflow-hidden bg-gray-100">
                  {opp.image && (
                    <img
                      src={opp.image}
                      alt={opp.title}
                      loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#0A1628]/10 text-[#00C2C2]">{opp.sector}</span>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 flex items-center gap-1">
                      <i className="ri-map-pin-2-line text-xs" /> {opp.country}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-[#0A1628] leading-snug mb-2 group-hover:text-[#00C2C2] transition-colors">{opp.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed flex-1 mb-4 line-clamp-3">{opp.description}</p>
                  {opp.investmentLevel && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
                      <i className="ri-funds-line text-[#00C2C2]" />
                      <span>{t("oppInvestment")}: <strong className="text-[#0A1628]">{opp.investmentLevel}</strong></span>
                    </div>
                  )}
                  <Link
                    to={`/business/opportunity/${opp.id}`}
                    className="mt-auto bg-[#0A1628] text-white text-xs font-bold px-4 py-2.5 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap text-center flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    {t("oppViewDetails")} <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
